import { z } from 'zod';

// ═══════════════════════════════════════════════════
//  Oversized cost rules
// ═══════════════════════════════════════════════════
//
// Per component type, per dimension, a short table of "from X inches -> factor"
// rows. A row applies from its `from` (inclusive) up to the next row's `from`,
// and the last row applies to anything larger. What a row does to a price is
// resolveOversize's business (utils/oversize.ts); this file only decides what
// may be stored in `oversize_settings.rules`.

/** The component types an oversized factor can apply to. Arms are out of scope. */
export const OVERSIZE_PRODUCT_TYPES = [
  'banquette_base',
  'banquette_seat_cushion',
  'banquette_back_cushion',
] as const;
export type OversizeProductType = typeof OVERSIZE_PRODUCT_TYPES[number];

/** Width is SECTION width — run inches over section count, one physical piece. */
export const OVERSIZE_AXES = ['width', 'depth', 'height'] as const;
export type OversizeAxis = typeof OVERSIZE_AXES[number];

/**
 * One step. `factor` 1 is allowed — an explicit "no charge from here" — but
 * below 1 is not, so a rule can never become a discount. `.strict()` rejects
 * an upper bound: rows are "from" only.
 */
export const OversizeRuleRowSchema = z.object({
  from: z.number().positive(),
  factor: z.number().min(1),
}).strict();

/**
 * One axis's steps, strictly ascending by `from`. Rejected rather than sorted,
 * so a stored table always reads in the order resolveOversize walks it. The
 * page sorts before saving. Factors are not required to rise with size.
 */
export const OversizeAxisRulesSchema = z.array(OversizeRuleRowSchema).max(50)
  .superRefine((rows, ctx) => {
    for (let i = 1; i < rows.length; i++) {
      const prev = rows[i - 1].from;
      const cur = rows[i].from;
      if (cur === prev) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate 'from' ${cur}: each row must start at a different size`,
          path: [i, 'from'],
        });
      } else if (cur < prev) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Rows must be in ascending order of 'from' (${cur} follows ${prev})`,
          path: [i, 'from'],
        });
      }
    }
  });

/** An axis with no rows never charges, so every axis defaults to none. */
export const OversizeTypeRulesSchema = z.object({
  width: OversizeAxisRulesSchema.default([]),
  depth: OversizeAxisRulesSchema.default([]),
  height: OversizeAxisRulesSchema.default([]),
}).strict();

/**
 * The whole `rules` column. A missing type has no rules (factor 1). Strict at
 * every level, so a typo in a hand-edited row fails parseOversizeRules — which
 * prices the in-scope types at null — instead of silently dropping a charge.
 */
export const OversizeRulesSchema = z.object({
  banquette_base: OversizeTypeRulesSchema.optional(),
  banquette_seat_cushion: OversizeTypeRulesSchema.optional(),
  banquette_back_cushion: OversizeTypeRulesSchema.optional(),
}).strict();

export const OversizeSettingsSchema = z.object({
  id: z.string().uuid(),
  rules: OversizeRulesSchema,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

/** PATCH replaces `rules` whole; there is no per-type merge. */
export const UpdateOversizeSettingsSchema = z.object({
  rules: OversizeRulesSchema,
});

export type OversizeRuleRow = z.infer<typeof OversizeRuleRowSchema>;
export type OversizeTypeRules = z.infer<typeof OversizeTypeRulesSchema>;
export type OversizeRules = z.infer<typeof OversizeRulesSchema>;
export type OversizeSettings = z.infer<typeof OversizeSettingsSchema>;
export type UpdateOversizeSettings = z.infer<typeof UpdateOversizeSettingsSchema>;
